import axios from "src/utils/axios";
import { createSlice } from "@reduxjs/toolkit";
import { localISODate } from "src/utils/localDate";

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: false,
  employee: null,
  employees: [],
  currentActiveEmployee: null,
  openEmployeeModal: null,
  openClockingModal: null,
  // 2026-10-07 · Explicación del fichaje abierto automáticamente al
  // identificarse («Tu turno empezó a las 16:00»…); se limpia al cerrar.
  clockingDueNotice: null,
  currentTab: "employee",
  clockings: [],
  currentClocking: null,
  searchClockings: [],

  scheduleConfig: null,
  scheduleConfigLoading: false,

  listFilter: "active", // "all" | "active" | "inactive"
  listPage: 0,
  listRowsPerPage: 10,

  calendario: [],
  calendarioLoading: false,

  // 2026-09-14 · Fase 2. Balance contractual semanal — se refresca
  // cada vez que el calendario cambia de semana visible. Contiene el
  // agregado del equipo + array de balances individuales.
  contractBalance: null,
  contractBalanceLoading: false,

  alertas: [],
  alertasLoading: false,

  kioskOpen: false,
  kioskManual: false,
  // Intención con la que se abrió el kiosk. Si vale "clocking", al
  // identificarse el `KioskOverlay` abre automáticamente el modal de
  // fichaje en vez de quedarse en la pantalla previa — atajo del
  // botón "Fichar" del topbar cuando el usuario aún no estaba
  // identificado. null = no hay intent (cierre normal).
  kioskIntent: null,
  // 2026-10-06 · Aviso informativo del kiosko (p. ej. «Carlos ya está siendo
  // utilizado en otro dispositivo («Barra»)…»): { reason, employeeName, deviceName }.
  kioskNotice: null,
  // 2026-10-07 · «Fichar» desde un kiosko forzado (sin empleado en la
  // tablet): al cerrar el modal de fichaje se vuelve al kiosko. Fichar no
  // identifica a nadie en el dispositivo.
  kioskReturnAfterClocking: false,

  // Adjustments PENDING del empleado actualmente identificado. Lo
  // poblamos tras `setEmployee` (en el flujo del kiosk) — si trae
  // elementos, el `KioskOverlay` abre automáticamente
  // `PendingSignaturesModal` para que el empleado firme/rechace ahí
  // mismo en lugar de esperar al email. Cuando se firma o rechaza
  // uno, lo quitamos del array; cuando llega a 0, el modal se cierra.
  pendingSignatures: [],
  pendingSignaturesLoading: false,
};

// ----------------------------------------------------------------------

const slice = createSlice({
  name: "employees",
  initialState,
  reducers: {
    // LOADING
    startLoading(state) {
      state.isLoading = true;
    },
    stopLoading(state) {
      state.isLoading = false;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    changeTab(state, action) {
      state.currentTab = action.payload;
    },

    addAdminEmployee(state, action) {
      // check if admin already exists
      let adminExists = state.employees.find((u) => u.isAdmin === true);

      // if admin exist update it with new data and return state
      if (adminExists) {
        state.employees = state.employees.map((u) => {
          if (u.isAdmin === true) return action.payload;
          else
            return {
              ...u,
              working: false,
            };
        });
      } else {
        // if admin not exist add it to state
        state.employees = state.employees.map((u) => ({
          ...u,
          working: false,
        }));

        state.employees = [...state.employees, action.payload];
      }

      state.currentActiveEmployee = action.payload.id;
    },

    changeEmployee(state, action) {
      state.currentActiveEmployee = action.payload;
      state.employees = state.employees.map((u) => {
        if (u.id == action.payload) return { ...u, working: true };
        else return { ...u, working: false };
      });
    },

    // CRUD
    getAllEmployeesSuccess(state, action) {
      // find admin employee inside employees
      let adminEmployee = state.employees.find((u) => u.isAdmin);

      // if admin employee exist add it to state with the rest of employees in action.payload
      if (adminEmployee) {
        let employees = [...action.payload, adminEmployee];
        state.employees = employees;
      } else state.employees = action.payload;

      if (state.employee != null) {
        const newEmployeeUpdate = state.employees.find(
          (u) => u.id === state.employee.id
        );

        if (newEmployeeUpdate != null)
          state.employee = {
            ...state.employee,
            ...newEmployeeUpdate,
          };
      }

      // clockings = TODOS los WorkScheduleItems de HOY + TODOS los que
      // contienen fichajes olvidados (últimos 4 días). Sin esto, turnos
      // partidos (ej. 09-13 y 17-21) se perdían — solo se veía el que
      // matcheaba la hora actual — y los olvidados de días anteriores
      // quedaban invisibles porque `todayWorkItems` sólo filtra HOY.
      //
      // Merge order: hoy primero, luego los olvidados que NO estén ya
      // en la lista de hoy (dedupe por id). Fallback al currentWorkItem
      // para compat con backends antiguos que aún no manden
      // `pendingOpenShifts`.
      state.clockings = action.payload.flatMap((employee) => {
        const today = Array.isArray(employee.todayWorkItems)
          ? employee.todayWorkItems
          : [];
        const pending = Array.isArray(employee.pendingOpenShifts)
          ? employee.pendingOpenShifts
          : [];
        const list = today.map((wi) => ({ ...wi, employeeId: employee.id }));
        // Añadimos primero los olvidados por delante — el reducer
        // `getCurrentClockingSuccess` prioriza el que tenga shift abierto,
        // así el foco natural es el más reciente pendiente.
        for (const wi of pending) {
          if (!list.some((x) => x.id === wi.id)) {
            list.unshift({ ...wi, employeeId: employee.id });
          }
        }
        // Fallback compat: si el backend no manda pendingOpenShifts,
        // seguimos aceptando currentWorkItem para no romper.
        const current = employee.currentWorkItem;
        if (current && !list.some((wi) => wi.id === current.id)) {
          list.unshift({ ...current, employeeId: employee.id });
        }
        return list;
      });

      state.isLoading = false;
      // state.isLoading = false; eliminar !!
    },
    getEmployeeSuccess(state, action) {
      state.employee = state.employees.find((u) => u.id === action.payload);
    },
    addEmployeeSuccess(state, action) {
      state.employee = action.payload;
      state.employees = [...state.employees, action.payload];
    },
    updateEmployeeSuccess(state, action) {
      state.employee = null;
      state.employees = state.employees.map((employee) => {
        if (employee.id == action.payload.id) return action.payload;
        else return employee;
      });
    },
    removeEmployeeSuccess(state, action) {
      state.employees = state.employees.map((employee) => {
        if (employee.id === action.payload)
          return {
            ...employee,
            isActive: false,
          };

        return employee;
      });
    },
    activateEmployeeSuccess(state, action) {
      state.employees = state.employees.map((employee) => {
        if (employee.id === action.payload)
          return {
            ...employee,
            isActive: true,
            deleteDate: null,
          };

        return employee;
      });
    },

    resetEmployee(state) {
      state.employee = null;
    },

    cleanData(state) {
      state.employee = null;
      state.employees = [];
      state.currentActiveEmployee = null;
    },

    setEmployee(state, action) {
      const id = action.payload;

      const employees = state.employees.map((u) => {
        if (u.id == id) return { ...u, working: true };
        else return { ...u, working: false };
      });

      state.employee = employees.find((u) => u.id === id);
      state.employees = employees;

      state.currentActiveEmployee = id;
    },

    changeOpenEmployeeModal(state, action) {
      state.openEmployeeModal = action.payload;
    },

    changeOpenClockingModal(state, action) {
      state.openClockingModal = action.payload;
      if (!action.payload) state.clockingDueNotice = null;
    },
    setClockingDueNotice(state, action) {
      state.clockingDueNotice = action.payload || null;
    },
    getClockingsSuccess(state, action) {
      state.searchClockings = action.payload;
    },
    updateSearchClockings(state, action) {
      const data = action.payload;

      state.searchClockings = state.searchClockings.map((clocking) => {
        if (clocking.id === data.id) {
          if (data.breakId) {
            return {
              ...clocking,
              breaks: clocking.breaks.map((b) => {
                if (b.id === data.breakId) {
                  return {
                    ...b,
                    breakEnd: data.end,
                  };
                } else return b;
              }),
            };
          }

          return {
            ...clocking,
            checkOutTime: data.end,
          };
        }

        return clocking;
      });
    },
    getCurrentClockingSuccess(state, action) {
      const employeeId = action.payload;

      if (!employeeId) return;
      if (state.clockings.length === 0) return;

      // Si el empleado tiene varios workItems hoy (turno partido),
      // priorizamos: (1) el que tenga shift abierto sin checkout,
      // (2) el que esté ahora mismo (Start <= now <= End),
      // (3) el siguiente futuro, (4) el primero. El usuario puede
      // alternar entre turnos desde la UI de tabs.
      const ofEmployee = state.clockings.filter((c) => c.employeeId === employeeId);
      if (ofEmployee.length === 0) {
        state.currentClocking = null;
        return;
      }

      const now = new Date().getTime();
      const open = ofEmployee.find((c) =>
        (c.shifts || []).some((s) => s.checkInTime && !s.checkOutTime)
      );
      if (open) {
        state.currentClocking = open;
        return;
      }
      const current = ofEmployee.find((c) => {
        const start = new Date(c.start).getTime();
        const end = new Date(c.end).getTime();
        return start <= now && now <= end;
      });
      if (current) {
        state.currentClocking = current;
        return;
      }
      const upcoming = ofEmployee.find((c) => new Date(c.start).getTime() > now);
      state.currentClocking = upcoming || ofEmployee[0];
    },

    // Permite cambiar manualmente entre los workItems del día (tabs en UI).
    selectClockingByIdSuccess(state, action) {
      const workItemId = action.payload;
      const found = state.clockings.find((c) => c.id === workItemId);
      if (found) state.currentClocking = found;
    },
    addClockingSuccess(state, action) {
      state.clockings = [...state.clockings, action.payload];
    },
    updateClockingSuccess(state, action) {
      state.clockings = state.clockings.map((clocking) => {
        if (clocking.id == action.payload.id)
          return {
            ...clocking,
            ...action.payload,
          };
        else return clocking;
      });

      if (state.currentClocking.id == action.payload.id)
        state.currentClocking = {
          ...state.currentClocking,
          ...action.payload,
        };
    },
    setListFilter(state, action) {
      state.listFilter = action.payload;
      state.listPage = 0;
    },
    setListPage(state, action) {
      state.listPage = action.payload;
    },
    setListRowsPerPage(state, action) {
      state.listRowsPerPage = action.payload;
      state.listPage = 0;
    },

    startScheduleConfigLoading(state) {
      state.scheduleConfigLoading = true;
    },
    getScheduleConfigSuccess(state, action) {
      state.scheduleConfigLoading = false;
      state.scheduleConfig = action.payload;
    },
    scheduleConfigError(state) {
      state.scheduleConfigLoading = false;
    },
    clearScheduleConfig(state) {
      state.scheduleConfig = null;
    },

    startCalendarioLoading(state) {
      state.calendarioLoading = true;
    },
    getCalendarioSuccess(state, action) {
      state.calendarioLoading = false;
      state.calendario = action.payload;
    },
    calendarioError(state) {
      state.calendarioLoading = false;
    },

    startContractBalanceLoading(state) {
      state.contractBalanceLoading = true;
    },
    getContractBalanceSuccess(state, action) {
      state.contractBalanceLoading = false;
      state.contractBalance = action.payload;
    },
    contractBalanceError(state) {
      state.contractBalanceLoading = false;
    },

    startAlertasLoading(state) {
      state.alertasLoading = true;
    },
    getAlertasSuccess(state, action) {
      state.alertasLoading = false;
      state.alertas = action.payload;
    },
    alertasError(state) {
      state.alertasLoading = false;
    },
    resolverAlertaSuccess(state, action) {
      state.alertas = state.alertas.map((a) =>
        a.id === action.payload ? { ...a, resuelta: true } : a
      );
    },
    updateCalendarioDia(state, action) {
      const { employeeId, fecha, dia } = action.payload;
      state.calendario = state.calendario.map((emp) => {
        if (emp.id !== employeeId) return emp;
        return {
          ...emp,
          dias: emp.dias.map((d) =>
            d.fecha.substring(0, 10) === fecha ? dia : d,
          ),
        };
      });
    },
    removeWorkItemFromCalendario(state, action) {
      const { employeeId, workItemId } = action.payload;
      state.calendario = state.calendario.map((emp) => {
        if (emp.id !== employeeId) return emp;
        return {
          ...emp,
          dias: emp.dias.map((d) =>
            d.workScheduleItemId === workItemId
              ? { ...d, workScheduleItemId: null, horaEntradaReal: null, horaSalidaReal: null, estado: d.employeeScheduleId ? "PROGRAMADO" : "LIBRE", esAdHoc: false }
              : d,
          ),
        };
      });
    },
    openKiosk(state, action) {
      state.kioskOpen = true;
      state.kioskManual = action.payload?.manual ?? true;
      state.kioskIntent = action.payload?.intent ?? null;
      state.kioskNotice = action.payload?.notice ?? null;
    },
    setKioskIntent(state, action) {
      state.kioskIntent = action.payload ?? null;
    },
    setKioskReturnAfterClocking(state, action) {
      state.kioskReturnAfterClocking = !!action.payload;
    },
    setKioskNotice(state, action) {
      state.kioskNotice = action.payload ?? null;
    },
    closeKiosk(state) {
      state.kioskOpen = false;
      state.kioskManual = false;
      state.kioskIntent = null;
      state.kioskNotice = null;
    },

    // ── Firma diferida — recordatorio en kiosk ─────────────────────────
    pendingSignaturesLoading(state, action) {
      state.pendingSignaturesLoading = action.payload;
    },
    pendingSignaturesSuccess(state, action) {
      state.pendingSignatures = action.payload || [];
      state.pendingSignaturesLoading = false;
    },
    /**
     * Quita un adjustment del array tras firmar/rechazar. El modal lee la
     * lista del slice; cuando llega a 0, se cierra solo. Sin esto tendríamos
     * que refetchear al backend tras cada acción.
     */
    removePendingSignature(state, action) {
      state.pendingSignatures = (state.pendingSignatures || []).filter(
        (a) => a.id !== action.payload,
      );
    },
    clearPendingSignatures(state) {
      state.pendingSignatures = [];
    },
  },
});

// ----------------------------------------------------------------------

// Reducer
export default slice.reducer;

// Actions
export const {
  changeEmployee,
  updateEmployeeSuccess,
  addEmployeeSuccess,
  getEmployeeSuccess,
  resetEmployee,
  addAdminEmployee,
  cleanData,
  setEmployee,
  changeOpenEmployeeModal,
  changeOpenClockingModal,
  setClockingDueNotice,
  changeTab,
  getClockingsSuccess,
  getCurrentClockingSuccess,
  selectClockingByIdSuccess,
  addClockingSuccess,
  updateClockingSuccess,
  updateSearchClockings,
  clearScheduleConfig,
  setListFilter,
  setListPage,
  setListRowsPerPage,
  resolverAlertaSuccess,
  openKiosk,
  setKioskIntent,
  setKioskReturnAfterClocking,
  setKioskNotice,
  closeKiosk,
  pendingSignaturesLoading,
  pendingSignaturesSuccess,
  removePendingSignature,
  clearPendingSignatures,
} = slice.actions;

// ----------------------------------------------------------------------

export function getCalendario(desde, hasta) {
  return async (dispatch) => {
    dispatch(slice.actions.startCalendarioLoading());
    try {
      const params = new URLSearchParams({ desde, hasta });
      const response = await axios.get(`employeeScheduleConfig/calendar?${params}`);
      dispatch(slice.actions.getCalendarioSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.calendarioError());
    }
  };
}

// 2026-09-14 · Fase 2 · Balance contractual semanal.
// `weekOf` puede ser cualquier día de la semana (el backend calcula
// lun-dom). El endpoint devuelve DtoTeamContractBalance con el
// agregado y el array de balances por empleado.
export function getContractBalance(weekOf) {
  return async (dispatch) => {
    dispatch(slice.actions.startContractBalanceLoading());
    try {
      const params = new URLSearchParams({ weekOf });
      const response = await axios.get(`employeeScheduleConfig/contract-balance?${params}`);
      dispatch(slice.actions.getContractBalanceSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.contractBalanceError());
    }
  };
}

export function addWorkItemCalendario(dto) {
  return async (dispatch) => {
    try {
      const response = await axios.post(`employeeScheduleConfig/workitem`, dto);
      const dia = response.data;
      dispatch(
        slice.actions.updateCalendarioDia({
          employeeId: dto.employeeId,
          fecha: localISODate(new Date(dto.fecha)),
          dia,
        }),
      );
      return dia;
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      throw error;
    }
  };
}

/**
 * 2026-09-28 · Delete de turno con scope. `scope` puede ser:
 *   - "OnlyDay" (default): solo este turno.
 *   - "ThisWeek": todos los turnos de la misma semana ISO.
 *   - "FutureSeries": este + todos los futuros de la misma serie.
 *   - "AllSeries": toda la serie, incluidos pasados (admin only).
 * En todos los casos el backend PRESERVA turnos con fichaje real y
 * turnos en curso. Devuelve `{ deleted, scope }` desde el backend.
 */
// Devuelve { deleted, scope, blockedWithShift, blockedInProgress }. Solo
// quita el turno de la vista si el backend lo borró de verdad; los en curso
// sin fichaje requieren `force` (confirmación del usuario).
export function deleteWorkItemCalendario(workItemId, employeeId, scope, force = false) {
  return async (dispatch) => {
    try {
      const params = new URLSearchParams();
      if (scope && scope !== "OnlyDay") params.set("scope", scope);
      if (force) params.set("force", "true");
      const q = params.toString() ? `?${params}` : "";
      const res = await axios.delete(
        `employeeScheduleConfig/workitem/${workItemId}${q}`,
      );
      if ((res?.data?.deleted ?? 0) > 0) {
        dispatch(slice.actions.removeWorkItemFromCalendario({ employeeId, workItemId }));
      }
      return res?.data;
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      throw error;
    }
  };
}

// 2026-09-14 · Fase 1 · Mover/redimensionar workItem para la vista Gantt.
// Devuelve el DtoCalendarioDia actualizado; el caller decide si
// refetch calendario completo o mutar localmente.
/**
 * 2026-09-28 · Update con scope. `scope`:
 *   - "OnlyDay" (default): solo este turno, marca IsOverride=true.
 *   - "FutureSeries": el turno + todos los futuros de la misma serie
 *     que caen en el mismo WeekIndex + DayOfWeek del ciclo rotativo.
 * Los WSI con fichaje real se preservan siempre. Devuelve
 * `{ anchor, updated, scope }`.
 */
export function updateWorkItemCalendario(dto, scope) {
  return async () => {
    const q = scope && scope !== "OnlyDay" ? `?scope=${scope}` : "";
    const response = await axios.put(
      `employeeScheduleConfig/workitem${q}`,
      dto,
    );
    return response.data;
  };
}

// F6 · Fase 5.5 · Restaurar planificación original de un turno editado.
// Borra el WSI y regenera desde la plantilla vigente. Rechaza si tiene
// fichaje real (BadRequest desde el backend).
export function restoreWorkItemCalendario(workItemId) {
  return async () => {
    await axios.post(`employeeScheduleConfig/workitem/${workItemId}/restore`);
  };
}

// F6 · Fase 5.7 · Cubrir turno de otro empleado. Crea un WSI ad-hoc en
// el empleado destino con las mismas horas + marca override + guarda
// CoversWorkItemId. Devuelve el DtoCalendarioDia del turno creado.
export function coverWorkItemCalendario(dto) {
  return async () => {
    const response = await axios.post(`employeeScheduleConfig/workitem/cover`, dto);
    return response.data;
  };
}

export function getScheduleConfig(employeeId) {
  return async (dispatch) => {
    dispatch(slice.actions.startScheduleConfigLoading());
    try {
      const response = await axios.get(`employeeScheduleConfig/${employeeId}`);
      dispatch(slice.actions.getScheduleConfigSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.scheduleConfigError());
    }
  };
}

export function upsertScheduleConfig(dto) {
  return async (dispatch) => {
    dispatch(slice.actions.startScheduleConfigLoading());
    try {
      const response = await axios.post(`employeeScheduleConfig/upsert`, dto);
      dispatch(slice.actions.getScheduleConfigSuccess(response.data));
      return response.data;
    } catch (error) {
      dispatch(slice.actions.scheduleConfigError());
      throw error;
    }
  };
}

export function upsertDaySchedule(dto) {
  return async (dispatch) => {
    try {
      await axios.post(`employeeScheduleConfig/day`, dto);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      throw error;
    }
  };
}

export function deleteDaySchedule(scheduleId) {
  return async (dispatch) => {
    try {
      await axios.delete(`employeeScheduleConfig/day/${scheduleId}`);
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function GetAllEmployees(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      var response = await axios.get(`employee`);

      dispatch(slice.actions.getAllEmployeesSuccess(response.data));

      if (id) {
        dispatch(setEmployee(id));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function GetEmployeeByID(employeeID) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.getEmployeeSuccess(employeeID));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function AddEmployee(employee) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      dispatch(slice.actions.addEmployeeSuccess(employee));

      const response = await axios.post(`employees`, JSON.stringify(employee), {
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "Access-Control-Allow-Origin": "*",
        },
      });
      dispatch(slice.actions.stopLoading());
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function UpdateEmployee(employee) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      dispatch(slice.actions.updateEmployeeSuccess(employee));

      const response = await axios.put(`employee`, JSON.stringify(employee), {
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "Access-Control-Allow-Origin": "*",
        },
      });
      dispatch(slice.actions.stopLoading());
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function RemoveEmployee(employeeID) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      dispatch(slice.actions.removeEmployeeSuccess(employeeID));

      await axios.delete(`employee/${employeeID}`, {
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "Access-Control-Allow-Origin": "*",
        },
      });
      dispatch(slice.actions.stopLoading());
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// Reactiva un empleado dado de baja. Endpoint dedicado que solo cambia
// IsActive=true — antes se usaba `UpdateEmployee` (PUT del DTO completo)
// pero devolvía 400 porque `DtoEmployeeUpdate` exige campos que la vista
// de listado no tiene rellenos (email, password, contrato…). El nuevo
// endpoint es POST /employee/Activate/{id} sin body.
export function ActivateEmployee(employeeID) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      await axios.post(`employee/Activate/${employeeID}`);
      // Actualiza el estado local: pone `isActive: true` en el
      // employee que corresponda dentro del array de Redux, sin tener
      // que refetchear toda la lista.
      dispatch(slice.actions.activateEmployeeSuccess(employeeID));
      dispatch(slice.actions.stopLoading());
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function GetClockings(data) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const response = await axios.post(`employeeShift/Search`, data);

      dispatch(slice.actions.getClockingsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function getAlertas(desde, hasta) {
  return async (dispatch) => {
    dispatch(slice.actions.startAlertasLoading());
    try {
      const params = new URLSearchParams({ desde, hasta });
      const response = await axios.get(`employeeScheduleConfig/alertas?${params}`);
      dispatch(slice.actions.getAlertasSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.alertasError());
    }
  };
}

export function resolverAlerta(alertaId) {
  return async (dispatch) => {
    try {
      await axios.put(`employeeScheduleConfig/alertas/${alertaId}/resolver`);
      dispatch(slice.actions.resolverAlertaSuccess(alertaId));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
    }
  };
}

// ── Firma diferida — recordatorio en kiosk ─────────────────────────────────

/**
 * Trae los adjustments PENDING (no vencidos) del empleado dado. El
 * `KioskOverlay` lo invoca tras la identificación con PIN; si la lista
 * tiene elementos, abre `PendingSignaturesModal` para que firme/rechace.
 */
export function GetPendingSignatures(employeeId) {
  return async (dispatch) => {
    if (!employeeId || employeeId === 0) {
      dispatch(slice.actions.pendingSignaturesSuccess([]));
      return;
    }
    dispatch(slice.actions.pendingSignaturesLoading(true));
    try {
      const response = await axios.get(
        `clocking-adjustment/pending-for/${employeeId}`,
      );
      dispatch(slice.actions.pendingSignaturesSuccess(response.data || []));
    } catch (error) {
      // Fail-soft: si el endpoint da error no bloqueamos al empleado entrando
      // al TPV. Los pendings volverán a aparecer en la próxima identificación.
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.pendingSignaturesSuccess([]));
    }
  };
}

/**
 * Firma un adjustment desde el kiosk. Tras el éxito quita el adjustment
 * de la lista del slice — el modal se cierra solo cuando la lista llega
 * a 0. Lanza si falla para que el modal pueda mostrar feedback.
 */
export function SignAdjustmentInternal(adjustmentId, employeeId, signature) {
  return async (dispatch) => {
    await axios.post(
      `clocking-adjustment/internal-sign/${adjustmentId}/${employeeId}`,
      { employeeSignature: signature },
    );
    dispatch(slice.actions.removePendingSignature(adjustmentId));
  };
}

/**
 * Rechaza un adjustment desde el kiosk. Mismo patrón que SignAdjustmentInternal.
 */
export function RejectAdjustmentInternal(adjustmentId, employeeId, reason) {
  return async (dispatch) => {
    await axios.post(
      `clocking-adjustment/internal-reject/${adjustmentId}/${employeeId}`,
      { rejectionReason: reason },
    );
    dispatch(slice.actions.removePendingSignature(adjustmentId));
  };
}

// ----------------------------------------------------------------------
